// A stand-in for the week-one endpoints, for local work without the service. The member, notes and plan in
// mock/view.json are synthetic (generated by ai-reporting's scripts/week_one_mock_view.py, so the shape is the real one).
// Jobs advance through their steps on timers. A note containing "fail" makes the job fail, to try the retry path.
import seed from "./mock/view.json";

const STEP_MS = 900;
const STEPS = {
  plan: ["reading the member's data", "reading the notes and data", "running comparisons", "writing the plan", "checking numbers and quotes"],
  revise: ["working out what the note is about", "reading the notes and data", "writing the plan", "checking numbers and quotes"],
  preview: ["writing", "checking the draft", "revising", "fitting the page"],
  write: ["writing", "checking the draft", "revising", "fitting the page", "saving to Drive"],
};

const clone = (x) => JSON.parse(JSON.stringify(x));
const nowIso = () => new Date().toISOString();
const store = { reports: { [seed.id]: clone(seed) }, undo: {}, previewPlan: {}, next: 1 };

function err(message, status = 400) {
  const e = new Error(message);
  e.status = status;
  return e;
}

function get(id) {
  const r = store.reports[id];
  if (!r) throw err(`no report ${id}`, 404);
  return r;
}

function runJob(id, kind, finish, params = {}) {
  const r = get(id);
  if (r.job && r.job.state === "running") throw err("a step is still running on this report", 409);
  const steps = STEPS[kind];
  r.job = { kind, state: "running", step: "starting", startedAt: nowIso(), updatedAt: nowIso(), error: null, params };
  steps.forEach((step, i) =>
    setTimeout(() => {
      r.job = { ...r.job, step, updatedAt: nowIso() };
    }, STEP_MS * (i + 1)),
  );
  setTimeout(() => {
    if (/fail/i.test(params.note || "")) {
      r.job = { ...r.job, state: "failed", error: "Simulated failure (the note contains \"fail\").", finishedAt: nowIso() };
      return;
    }
    finish(r);
    r.updatedAt = nowIso();
    r.job = { ...r.job, state: "done", step: "done", updatedAt: nowIso(), finishedAt: nowIso() };
  }, STEP_MS * (steps.length + 1));
}

function syncDoseConflicts(r) {
  const meds = Object.fromEntries((r.plan.medications || []).map((m) => [m.id, m]));
  (r.review.dose_conflicts || []).forEach((c) => {
    c.confirmed = (meds[c.id] || {}).confirmed_dose || null;
  });
}

function view(r) {
  const v = clone(r);
  delete v.job?.params;
  if (v.job) v.job.state = r.job.state;
  if (v.previewDraft) v.previewDraft.outOfDate = JSON.stringify(store.previewPlan[r.id]) !== JSON.stringify(r.plan);
  return v;
}

export async function listReports() {
  return Object.values(store.reports).map((r) => ({
    _id: r.id, stage: r.stage, status: r.status, reportDate: r.reportDate, createdAt: r.createdAt, updatedAt: r.updatedAt,
    approval: r.approval, job: r.job && { kind: r.job.kind, state: r.job.state, step: r.job.step, updatedAt: r.job.updatedAt },
  }));
}

// What a report started now would read (the service works it out from the member's timeline).
export async function coverage() {
  const st = seed.review.member.stage;
  return { firstName: seed.review.member.name, firstCall: st.window_start, start: st.window_start, end: st.window_end, calls: 3,
           sensorDays: 12, meals: 9, lastData: st.window_end, ready: true, why: null, notes: [] };
}

export async function logError() {}

// The report is dated the day it starts (as the service does).
export async function startReport(memberId) {
  const reportDate = new Date().toISOString().slice(0, 10);
  const id = `mock${String(store.next++).padStart(20, "0")}`;
  const r = { ...clone(seed), id, memberId, memberName: seed.review.member.name, reportDate, createdAt: nowIso(), updatedAt: nowIso(),
              plan: null, review: null, analyst: null, revisions: [], previewDraft: undefined, final: undefined, job: null };
  store.reports[id] = r;
  runJob(id, "plan", (x) => Object.assign(x, { plan: clone(seed.plan), review: clone(seed.review), analyst: clone(seed.analyst) }), {});
  return id;
}

export async function getReport(id) {
  return view(get(id));
}

export async function savePlan(id, plan) {
  const r = get(id);
  if (r.status !== "draft") throw err("this report is no longer a draft");
  r.plan = clone(plan);
  r.updatedAt = nowIso();
  syncDoseConflicts(r);
  return r.plan;
}

export async function revise(id, { note = "", statuses = null, lever = null, before: was = null }) {
  const r = get(id);
  const before = clone(r.plan); // undo goes back to before the reviewer's change, not just before the update
  if (was && was.statuses) before.differential.forEach((c) => { if (c.name in was.statuses) c.status = was.statuses[c.name]; });
  if (was && was.lever != null) before.lever.action = was.lever;
  let kind = "note", changed = ["headline"], scope = ["headline"], fullNote = note;
  runJob(id, "revise", (x) => {
    if (statuses) {
      kind = "statuses";
      changed = ["finding", "lever", "check", "headline"];
      scope = ["differential", "finding", "lever", "check", "chart", "headline", "longer_game", "doctor_ask"];
      fullNote = Object.entries(statuses).map(([n, s]) => `The reviewer changed the status of '${n}' to ${s.replace(/_/g, " ")}.`).join(" ");
      x.plan.finding.summary = `${x.plan.finding.summary} (Updated for the status change.)`;
      x.plan.check.criteria = `${x.plan.check.criteria} (Updated.)`;
    } else if (lever) {
      kind = "lever";
      changed = ["lever", "check", "headline"];
      scope = ["lever", "check", "headline"];
      fullNote = `The reviewer wrote this week's action themselves: "${lever}".`;
      x.plan.lever.action = lever;
      x.plan.lever.measured_by = "the sensor's readings after the action (updated to fit)";
      x.plan.check.criteria = "Compare days with the new action and days without. (Updated to fit.)";
    }
    x.plan.headline = `${x.plan.headline.replace(/ \(revised\)$/, "")} (revised)`;
    store.undo[id] = [...(store.undo[id] || []), before];
    x.revisions = [...x.revisions, { at: nowIso(), note: fullNote, kind, scope, changed, seconds: 4.2 }];
  }, { note, statuses, lever, before: was });
}

export async function undo(id) {
  const r = get(id);
  const stack = store.undo[id] || [];
  if (!stack.length) throw err("no revision to undo");
  r.plan = stack.pop();
  r.revisions = r.revisions.slice(0, -1);
  return r.plan;
}

export async function preview(id, note = "") {
  note = (note || "").trim();
  if (note && !get(id).previewDraft) throw err("preview the report first; preview feedback is about the written report");
  runJob(id, "preview", (x) => {
    store.previewPlan[id] = clone(x.plan);
    if (note) x.writerNotes = [...(x.writerNotes || []), { at: nowIso(), note }];
    x.previewDraft = { at: nowIso(), pages: 2, cuts: [], seconds: 3.6, tokens: {}, note: note || null,
      leftovers: [{ slot: "check_text", problem: "says \"about two weeks\"; the check reads on day 7" }] };
  }, { note });
}

export async function approve(id, { approverName, purpose, plan }) {
  const r = get(id);
  if (r.job && r.job.state === "running") throw err("a step is still running on this report", 409);
  const p = plan || r.plan;
  const missing = [];
  if (!(approverName || "").trim()) missing.push("the approver's name");
  if (!["member", "other"].includes(purpose)) missing.push("who it's for (member or other)");
  (p.placeholders || []).forEach((x) => { if (x.required && !(x.value || "").trim()) missing.push(`fill "${x.name}"`); });
  const confirmed = Object.fromEntries((p.medications || []).map((m) => [m.id, m.confirmed_dose]));
  (r.review.dose_conflicts || []).forEach((c) => { if (!confirmed[c.id]) missing.push(`choose ${c.name}'s dose`); });
  if (missing.length) throw err(`still needed: ${missing.join(", ")}`);
  r.plan = clone(p);
  r.status = "approved";
  r.approval = { name: approverName.trim(), purpose, at: nowIso() };
  const fromPreview = !!r.previewDraft && JSON.stringify(store.previewPlan[id]) === JSON.stringify(r.plan);
  runJob(id, "write", (x) => {
    x.status = "ready";
    x.previewDraft = undefined;
    x.final = { at: nowIso(), pages: 2, pages_planned: 2, cuts: [], shortened: [], checks_final: [], seconds: fromPreview ? 0.1 : 21.4,
                fromPreview, driveFileId: purpose === "member" ? "mock-drive-file" : null };
  });
}

export async function retry(id) {
  const r = get(id);
  if (!r.job || r.job.state !== "failed") throw err("only a failed or interrupted step can be retried");
  const { kind, params } = r.job;
  r.job.state = "done";
  if (kind === "revise") await revise(id, { ...params, note: (params.note || "").replace(/fail/gi, "") });
  else if (kind === "preview") await preview(id, (params && params.note) || "");
  return id;
}

// A one-page PDF that says what it is.
export async function pdfBlob(id, which) {
  const r = get(id);
  if (which === "final" ? !r.final : !r.previewDraft) throw err(`No ${which} PDF yet`, 404);
  const text = `Mock ${which === "final" ? "final report" : "preview draft"}: synthetic member, no real PDF`;
  const objs = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>",
    null,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  ];
  const stream = `BT /F1 16 Tf 72 720 Td (${text}) Tj ET`;
  objs[3] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  let pdf = "%PDF-1.4\n";
  const offsets = objs.map((o, i) => {
    const at = pdf.length;
    pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
    return at;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, "0")} 00000 n \n`).join("")}`;
  pdf += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new Blob([pdf], { type: "application/pdf" });
}
